import { createContext, useContext, useSyncExternalStore } from "react";

import {
  EMPTY_SNAPSHOT,
  type ScreenLayoutSnapshot,
  type ScreenLayoutStore,
} from "./screen-layout-store";

/**
 * Carries the per-ScreenLayout store. The context value is the store
 * object itself — a stable reference that never changes — so consuming
 * the context never causes a re-render. Reactive reads go through the
 * store's `subscribe` / `getSnapshot` via `useSyncExternalStore`.
 */
export const ScreenLayoutContext = createContext<ScreenLayoutStore | null>(null);

export function useScreenLayoutStore(componentName: string): ScreenLayoutStore {
  const store = useContext(ScreenLayoutContext);
  if (!store) {
    throw new Error(`<${componentName}> must be rendered inside <ScreenLayout>.`);
  }
  return store;
}

export function useOptionalScreenLayoutStore(): ScreenLayoutStore | null {
  return useContext(ScreenLayoutContext);
}

const subscribeNoop = () => () => {};
const getEmptySnapshot = () => EMPTY_SNAPSHOT;

export function useScreenLayoutSnapshot(store: ScreenLayoutStore | null): ScreenLayoutSnapshot {
  return useSyncExternalStore(
    store?.subscribe ?? subscribeNoop,
    store?.getSnapshot ?? getEmptySnapshot,
    store?.getSnapshot ?? getEmptySnapshot,
  );
}
